
import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, makeStyles, Chip, Tabs, Tab, TextField, Select, MenuItem, FormControl, InputLabel } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import AppButton from "components/CustomButtons/AppButton";
import AppColor from "utilities/AppColor";
import AppSelect from "components/Select/AppSelect";
import AppDialog from "components/Dialog/AppDialog";
import { fetchPageIncident, getIncidentList } from "store/slices/incident";
import { INCIDENT_STATUS, INCIDENT_SEVERITY } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  container: {
    padding: "20px",
    backgroundColor: '#F5F5F5'
  },
  titleRow: {
    marginBottom: "14px",
  },
  mainTitle: {
    fontSize: "22px",
    fontWeight: 700,
    marginBottom: "4px",
    color: "#0F766E",
  },
  subtitle: {
    fontSize: "12px",
    color: "#6B7280",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3,1fr)",
    gap: "8px",
    marginBottom: "14px",
  },
  statCard: {
    borderRadius: "8px",
    padding: "10px",
    textAlign: "center",
  },
  statNumber: {
    fontSize: "18px",
    fontWeight: 700,
    marginBottom: "4px",
  },
  statLabel: {
    fontSize: "11px",
  },
  filtersRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "14px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  tableCard: {
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#6B7280",
    borderBottom: "1px solid #E5E7EB",
    background: "#FAFAFA",
    "&:first-child": {
      paddingLeft: "12px",
    },
  },
  td: {
    padding: "8px 10px",
    color: "#111827",
    fontSize: "12px",
    borderBottom: "1px solid #F3F4F6",
    "&:first-child": {
      paddingLeft: "12px",
    },
  },
  overdueRow: {
    background: "#FFF5F5",
  },
  badgeRed: {
    background: "#FEE2E2",
    color: "#991b1b",
  },
  badgeGreen: {
    background: "#CCFBF1",
    color: "#0F766E",
  },
  badgeAmber: {
    background: "#FEF3C7",
    color: "#92400E",
  },
  badgeBlue: {
    background: "#DBEAFE",
    color: "#1E40AF",
  },
  badgePurple: {
    background: "#EDE9FE",
    color: "#5B21B6",
  },
  badgeTeal: {
    background: "#CCFBF1",
    color: "#0F766E",
  },
  badgeGrey: {
    background: "#F3F4F6",
    color: "#6B7280",
  },
  tabContainer: {
    borderBottom: "1px solid #E5E7EB",
    "& .MuiTab-root": {
      fontSize: "12px",
      fontWeight: 500,
      color: "#6B7280",
      padding: "9px 16px",
      textTransform: "none",
      minHeight: "auto",
      "&.Mui-selected": {
        color: "#0F766E",
        fontWeight: 600,
      },
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#2DD4BF",
      height: "2.5px",
    },
  },
  tabContent: {
    padding: "16px",
  },
  abox: {
    marginBottom: "12px",
    borderRadius: "8px",
    padding: "9px 11px",
    fontSize: "11px",
    display: "flex",
    gap: "7px",
    alignItems: "flex-start",
  },
  aboxAt: {
    background: "#F0FDFA",
    border: "1px solid #99F6E4",
    color: "#0F766E",
  },
  aboxAbl: {
    background: "#EFF6FF",
    border: "1px solid #BFDBFE",
    color: "#1D4ED8",
  },
  abcGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "10px",
    marginBottom: "12px",
  },
  abcCard: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    padding: "12px",
  },
  abcLetter: {
    fontSize: "20px",
    fontWeight: 800,
    marginBottom: "4px",
  },
  abcTitle: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "5px",
  },
  formControl: {
    width: "100%",
    marginBottom: "6px",
  },
  formLabel: {
    fontSize: "11px",
    fontWeight: 500,
    color: "#6B7280",
    marginBottom: "3px",
    display: "block",
  },
  formInput: {
    width: "100%",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    padding: "7px 10px",
    fontSize: "11px",
    background: "#fff",
    fontFamily: "inherit",
    "&:focus": {
      outline: "none",
      borderColor: "#2DD4BF",
    },
  },
  formTextarea: {
    width: "100%",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    padding: "7px 9px",
    fontSize: "11px",
    background: "#fff",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: "48px",
    "&:focus": {
      outline: "none",
      borderColor: "#2DD4BF",
    },
  },
  severityGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "6px",
    marginBottom: "14px",
  },
  severityBtn: {
    border: "1.5px solid #E5E7EB",
    borderRadius: "6px",
    padding: "8px 4px",
    textAlign: "center",
    cursor: "pointer",
    fontFamily: "inherit",
    background: "#fff",
    "&:hover": {
      borderColor: "#2DD4BF",
    },
    "&.active": {
      borderColor: "#2DD4BF",
      background: "#F0FDFA",
    },
  },
  severityLabel: {
    fontSize: "11px",
    fontWeight: 700,
    marginBottom: "2px",
  },
  severitySubLabel: {
    fontSize: "10px",
    color: "#6B7280",
  },
  formTopGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "10px",
    marginBottom: "12px",
  },
}));

const severityLabels = {
  [INCIDENT_SEVERITY.LOW]: "Low",
  [INCIDENT_SEVERITY.MODERATE]: "Moderate",
  [INCIDENT_SEVERITY.SERIOUS]: "Serious",
  [INCIDENT_SEVERITY.NOTIFIABLE]: "Notifiable",
};

const statusLabels = {
  [INCIDENT_STATUS.OPEN]: "Open",
  [INCIDENT_STATUS.UNDER_REVIEW]: "Under review",
  [INCIDENT_STATUS.RESOLVED]: "Resolved",
  [INCIDENT_STATUS.CLOSED]: "Closed",
};

const formatIncidentDate = (dateValue) => {
  if (!dateValue) return "-";
  const d = new Date(dateValue);
  if (isNaN(d.getTime())) return dateValue;
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short" });
};

const formatIncidentType = (incident) => {
  if (incident.incidentTypes && incident.incidentTypes.length) {
    return incident.incidentTypes
      .map((t) => (t || "").toString().replace(/_/g, " ").toLowerCase())
      .join(", ");
  }
  return incident.incidentTypeOther || "-";
};

const IncidentTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSeverity, setSelectedSeverity] = useState("Moderate");
  const [viewIncident, setViewIncident] = useState(null);

  const incidents = useSelector(getIncidentList);

  useEffect(() => {
    dispatch(fetchPageIncident({ page: 1, size: 50 }));
  }, [dispatch]);

  const incidentStats = useMemo(() => {
    const now = new Date();
    const openCount = incidents.filter((i) => i.status === INCIDENT_STATUS.OPEN).length;
    const underReviewCount = incidents.filter((i) => i.status === INCIDENT_STATUS.UNDER_REVIEW).length;
    const closedThisMonthCount = incidents.filter((i) => {
      if (i.status !== INCIDENT_STATUS.CLOSED || !i.updatedAt) return false;
      const updated = new Date(i.updatedAt);
      return updated.getMonth() === now.getMonth() && updated.getFullYear() === now.getFullYear();
    }).length;
    return { openCount, underReviewCount, closedThisMonthCount };
  }, [incidents]);

  const severityOptions = [
    { id: "Low", label: "Low", subLabel: "No harm", color: "#059669" },
    { id: "Moderate", label: "Moderate", subLabel: "Some distress", color: "#D97706" },
    { id: "Serious", label: "Serious", subLabel: "Harm or risk", color: "#DC2626" },
    { id: "Notifiable", label: "Notifiable", subLabel: "Commission", color: "#7C2D12" },
  ];

  const getSeverityBadgeClass = (severity) => {
    switch (severity) {
      case INCIDENT_SEVERITY.LOW:
        return classes.badgeBlue;
      case INCIDENT_SEVERITY.MODERATE:
        return classes.badgeAmber;
      case INCIDENT_SEVERITY.SERIOUS:
        return classes.badgeAmber;
      case INCIDENT_SEVERITY.NOTIFIABLE:
        return classes.badgeRed;
      default:
        return classes.badgeGrey;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case INCIDENT_STATUS.OPEN:
        return classes.badgeRed;
      case INCIDENT_STATUS.UNDER_REVIEW:
        return classes.badgeAmber;
      case INCIDENT_STATUS.RESOLVED:
        return classes.badgeBlue;
      case INCIDENT_STATUS.CLOSED:
        return classes.badgeGreen;
      default:
        return classes.badgeGrey;
    }
  };

  const getRowClass = (incident) => {
    if (incident.status === INCIDENT_STATUS.OPEN) {
      return classes.overdueRow;
    }
    return "";
  };

  const getActionLabel = (incident) => {
    if (incident.status === INCIDENT_STATUS.CLOSED) return "View";
    if (incident.status === INCIDENT_STATUS.UNDER_REVIEW || incident.status === INCIDENT_STATUS.RESOLVED) return "Update";
    return "Review";
  };

  const handleActionClick = (incident) => {
    if (incident.status === INCIDENT_STATUS.CLOSED) {
      setViewIncident(incident);
    } else {
      history.push(`/admin/incidents/${incident.id}`);
    }
  };

  const tabs = [
    { label: "Register" },
    { label: "ABC report form" },
    { label: "Monthly report" },
  ];

  return (
    <Box className={classes.container}>
      <Box className={classes.titleRow}>
        <Typography className={classes.mainTitle}>
          Incidents
        </Typography>
      </Box>

      <Box className={classes.statsGrid}>
        <Box
          className={classes.statCard}
          style={{ background: "#FEE2E2" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#DC2626" }}
          >
            {incidentStats.openCount}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#991B1B" }}
          >
            Open — review needed
          </Typography>
        </Box>
        <Box
          className={classes.statCard}
          style={{ background: "#FEF3C7" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#D97706" }}
          >
            {incidentStats.underReviewCount}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#92400E" }}
          >
            Under review
          </Typography>
        </Box>
        <Box
          className={classes.statCard}
          style={{ background: "#CCFBF1" }}
        >
          <Typography
            className={classes.statNumber}
            style={{ color: "#0F766E" }}
          >
            {incidentStats.closedThisMonthCount}
          </Typography>
          <Typography
            className={classes.statLabel}
            style={{ color: "#0F766E" }}
          >
            Closed this month
          </Typography>
        </Box>
      </Box>

      <Box className={classes.tableCard}>
        <Tabs
          className={classes.tabContainer}
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>

        {activeTab === 0 && (
          <Box className={classes.tabContent}>
            <Box style={{ overflowX: "auto" }}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>Ref</th>
                    <th className={classes.th}>Date</th>
                    <th className={classes.th}>Participant</th>
                    <th className={classes.th}>Type</th>
                    <th className={classes.th}>Severity</th>
                    <th className={classes.th}>Status</th>
                    <th className={classes.th}>Commission</th>
                    <th className={classes.th}>CI</th>
                    <th className={classes.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.length === 0 && (
                    <tr>
                      <td className={classes.td} colSpan={9} style={{ textAlign: "center", color: "#6B7280" }}>
                        No incidents found.
                      </td>
                    </tr>
                  )}
                  {incidents.map((incident) => (
                    <tr
                      key={incident.id}
                      className={getRowClass(incident)}
                    >
                      <td className={classes.td} style={{ fontWeight: 700 }}>
                        {incident.incidentNumber || `INC-${incident.id}`}
                      </td>
                      <td className={classes.td}>{formatIncidentDate(incident.incidentDate)}</td>
                      <td className={classes.td}>{incident.participantId ? `Participant #${incident.participantId}` : "-"}</td>
                      <td className={classes.td} style={{ textTransform: "capitalize" }}>{formatIncidentType(incident)}</td>
                      <td className={classes.td}>
                        <Chip
                          size="small"
                          label={severityLabels[incident.severity] || incident.severity}
                          className={getSeverityBadgeClass(incident.severity)}
                          style={{ padding: "3px" }}
                        />
                      </td>
                      <td className={classes.td}>
                        <Chip
                          size="small"
                          label={statusLabels[incident.status] || incident.status}
                          className={getStatusBadgeClass(incident.status)}
                          style={{ padding: "3px" }}
                        />
                      </td>
                      <td className={classes.td}>
                        <Chip
                          size="small"
                          label={incident.commissionReported ? "Reported" : incident.severity === INCIDENT_SEVERITY.NOTIFIABLE ? "Pending" : "Not required"}
                          className={incident.commissionReported ? classes.badgeGreen : incident.severity === INCIDENT_SEVERITY.NOTIFIABLE ? classes.badgeRed : classes.badgeGrey}
                          style={{ padding: "3px" }}
                        />
                      </td>
                      <td className={classes.td}>
                        <Chip
                          size="small"
                          label={incident.severity === INCIDENT_SEVERITY.NOTIFIABLE ? "Yes" : "No"}
                          className={incident.severity === INCIDENT_SEVERITY.NOTIFIABLE ? classes.badgeRed : classes.badgeGrey}
                          style={{ padding: "3px" }}
                        />
                      </td>
                      <td className={classes.td}>
                        <AppButton
                          label={getActionLabel(incident)}
                          buttonColor={AppColor.white}
                          style={{
                            fontSize: "11px",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            border: "1px solid #E5E7EB",
                            height: "24px",
                            minHeight: "24px",
                            color: "#0F766E",
                          }}
                          onClick={() => handleActionClick(incident)}
                          rounder
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box className={classes.tabContent}>
            <Box className={`${classes.abox} ${classes.aboxAt}`}>
              <i className="fas fa-qrcode" style={{ fontSize: "14px", flexShrink: 0, marginTop: "2px" }}></i>
              <span>QR code in each house opens this form. House and active shift pre-filled on mobile. No login required if worker is on shift.</span>
            </Box>

            <Box className={classes.formTopGrid}>
              <Box>
                <label className={classes.formLabel}>Participant</label>
                <select className={classes.formInput}>
                  <option>Sophie Rahman</option>
                  <option>James Mitchell</option>
                  <option>Tom Nguyen</option>
                </select>
              </Box>
              <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Box>
                  <label className={classes.formLabel}>Date</label>
                  <input type="date" className={classes.formInput} value="2026-06-03" />
                </Box>
                <Box>
                  <label className={classes.formLabel}>Time</label>
                  <input type="time" className={classes.formInput} value="13:45" />
                </Box>
              </Box>
            </Box>

            <Box className={classes.abcGrid}>
              <Box className={classes.abcCard}>
                <Typography className={classes.abcLetter} style={{ color: "#2563EB" }}>A</Typography>
                <Typography className={classes.abcTitle}>Antecedent — what happened before?</Typography>
                <select className={classes.formInput} style={{ marginBottom: "6px" }}>
                  <option>Change in routine</option>
                  <option>Sensory trigger</option>
                  <option>Unknown trigger</option>
                </select>
                <textarea className={classes.formTextarea} placeholder="Describe the context and trigger..."></textarea>
              </Box>
              <Box className={classes.abcCard}>
                <Typography className={classes.abcLetter} style={{ color: "#D97706" }}>B</Typography>
                <Typography className={classes.abcTitle}>Behaviour — exactly what occurred</Typography>
                <select className={classes.formInput} style={{ marginBottom: "6px" }}>
                  <option>Verbal aggression</option>
                  <option>Physical — person</option>
                  <option>Physical — property</option>
                </select>
                <textarea className={classes.formTextarea} placeholder="No interpretation — describe only what was observed..."></textarea>
              </Box>
              <Box className={classes.abcCard}>
                <Typography className={classes.abcLetter} style={{ color: "#059669" }}>C</Typography>
                <Typography className={classes.abcTitle}>Consequence — response and outcome</Typography>
                <select className={classes.formInput} style={{ marginBottom: "6px" }}>
                  <option>BSP strategy used</option>
                  <option>Space and quiet</option>
                  <option>Verbal de-escalation</option>
                </select>
                <textarea className={classes.formTextarea} placeholder="What did you do? How did the person settle?"></textarea>
              </Box>
            </Box>

            <Box style={{ marginBottom: "12px" }}>
              <Typography className={classes.formLabel}>Severity</Typography>
              <Box className={classes.severityGrid}>
                {severityOptions.map((option) => (
                  <button
                    key={option.id}
                    className={`${classes.severityBtn} ${selectedSeverity === option.id ? "active" : ""}`}
                    onClick={() => setSelectedSeverity(option.id)}
                  >
                    <Typography className={classes.severityLabel} style={{ color: option.color }}>
                      {option.label}
                    </Typography>
                    <Typography className={classes.severitySubLabel}>
                      {option.subLabel}
                    </Typography>
                  </button>
                ))}
              </Box>
            </Box>

            <AppButton
              label="Submit incident report"
              buttonColor={AppColor.mainColor}
              style={{ fontSize: "12px", padding: "7px 14px", borderRadius: "6px", display: "flex", alignItems: "center", gap: "5px" }}
              icon={<i className="fas fa-paper-plane" style={{ fontSize: "12px" }}></i>}
            />
          </Box>
        )}

        {activeTab === 2 && (
          <Box className={classes.tabContent}>
            <Box className={`${classes.abox} ${classes.aboxAbl}`}>
              <i className="fas fa-star" style={{ fontSize: "14px", flexShrink: 0, marginTop: "2px" }}></i>
              <span>AI-generated monthly report — May 2026. Drawn from incident register. Review before sending to NDIS Commission.</span>
            </Box>

            <Box style={{
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              padding: "14px",
              fontSize: "12px",
              color: "#374151",
              lineHeight: "1.7",
            }}>
              4 incidents in May 2026. 2 involved Sophie Rahman (Wattle Grove) — both verbal aggression during routine changes. Pattern suggests BSP review warranted. 1 property damage — Eucalyptus, under review. 1 fall — Banksia, resolved. 0 Commission-reportable incidents. All responded to within shift.
            </Box>

            <Box style={{ display: "flex", gap: "6px", marginTop: "10px" }}>
              <AppButton
                buttonColor={AppColor.mainColor}
                style={{ fontSize: "11px", padding: "4px 10px" }}
              >
                <i className="far fa-file mr-2" style={{ fontSize: "12px" }}></i> Export PDF
              </AppButton>
              <AppButton
                buttonColor={AppColor.white}
                style={{
                  fontSize: "11px",
                  padding: "4px 10px",
                  border: "1px solid #E5E7EB",
                  color: "#374151",
                }}
              >
                <i className="fas fa-envelope mr-2" style={{ fontSize: "12px" }}></i>Send to Commission
              </AppButton>
            </Box>
          </Box>
        )}
      </Box>

      <AppDialog
        open={!!viewIncident}
        title={viewIncident ? (viewIncident.incidentNumber || `INC-${viewIncident.id}`) : ""}
        onClose={() => setViewIncident(null)}
        maxWidth="sm"
      >
        {viewIncident && (
          <Box>
            <Typography style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Date</Typography>
            <Typography style={{ fontSize: 13, marginBottom: 12 }}>{formatIncidentDate(viewIncident.incidentDate)}</Typography>

            <Typography style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Type</Typography>
            <Typography style={{ fontSize: 13, marginBottom: 12, textTransform: "capitalize" }}>{formatIncidentType(viewIncident)}</Typography>

            <Typography style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Severity</Typography>
            <Chip size="small" label={severityLabels[viewIncident.severity] || viewIncident.severity} className={getSeverityBadgeClass(viewIncident.severity)} style={{ marginBottom: 12 }} />

            <Typography style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Status</Typography>
            <Box style={{ marginBottom: 12 }}>
              <Chip size="small" label={statusLabels[viewIncident.status] || viewIncident.status} className={getStatusBadgeClass(viewIncident.status)} />
            </Box>

            <Typography style={{ fontSize: 12, color: "#6B7280", marginBottom: 6 }}>Description</Typography>
            <Typography style={{ fontSize: 13, marginBottom: 12 }}>{viewIncident.antecedent || viewIncident.behaviour || "-"}</Typography>

            {viewIncident.status !== INCIDENT_STATUS.CLOSED && (
              <Box style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <AppButton
                  label="Go to Detail"
                  buttonColor={AppColor.mainColor}
                  rounder
                  onClick={() => {
                    setViewIncident(null);
                    history.push(`/admin/incidents/${viewIncident.id}`);
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </AppDialog>
    </Box>
  );
};

export default IncidentTable;
